import { useMemo } from 'react';
import {
  AudioLines,
  BotMessageSquare,
  Hash,
  Infinity as InfinityIcon,
  PhoneCall,
  Sparkles,
  Workflow,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useUserModules } from '../../api';
import {
  TEMPLATE_OPTIONS,
  type TemplateId,
  type VoiceCampaignFormValues,
} from '../../types';

interface Props {
  values: VoiceCampaignFormValues;
  setValues: (patch: Partial<VoiceCampaignFormValues>) => void;
  /** Block the template picker when editing an existing campaign. */
  lockTemplate?: boolean;
}

interface TemplateCard {
  value: TemplateId;
  label: string;
  description: string;
  icon: LucideIcon;
}

const TEMPLATE_DETAILS: Record<TemplateId, Omit<TemplateCard, 'value' | 'label'>> = {
  '0': {
    description: 'Single pre-recorded message',
    icon: AudioLines,
  },
  '1': {
    description: 'Menu + DTMF input',
    icon: Hash,
  },
  '2': {
    description: 'Patch to a live agent',
    icon: PhoneCall,
  },
  '3': {
    description: 'Custom IVR flow',
    icon: Workflow,
  },
  '7': {
    description: 'Text-to-speech IVR',
    icon: Sparkles,
  },
  '8': {
    description: 'TTS menu + DTMF',
    icon: BotMessageSquare,
  },
  '9': {
    description: 'TTS menu + agent patch',
    icon: BotMessageSquare,
  },
  '10': {
    description: 'Driven by a TTS Flow',
    icon: InfinityIcon,
  },
};

export function StepBasics({ values, setValues, lockTemplate = false }: Props) {
  const modules = useUserModules();
  const moduleNames = useMemo(
    () => new Set((modules.data ?? []).map((m) => m.moduleName)),
    [modules.data],
  );

  // Continuous Campaign is mutually exclusive with the other templates:
  // when the account has that module on, ONLY Continuous shows. Otherwise
  // all standard cards (Simple IVR, DTMF, Call Patch, Custom IVR, TTS) are
  // visible — the backend rejects compose if a required module is missing.
  const isContinuous = moduleNames.has('Continuous Campaign');

  const availableCards: TemplateCard[] = useMemo(() => {
    const out: TemplateCard[] = [];
    for (const o of TEMPLATE_OPTIONS) {
      if (isContinuous && o.value !== '10') continue;
      if (!isContinuous && o.value === '10') continue;
      // 8 and 9 are TTS sub-flavors of 7 — only 7 appears in the picker.
      if (o.value === '8' || o.value === '9') continue;
      const detail = TEMPLATE_DETAILS[o.value];
      if (!detail) continue;
      // The TTS parent card represents the whole 7/8/9 family — give it a
      // generic label rather than "TTS Simple IVR" (which is a sub-flavor).
      const label = o.value === '7' ? 'TTS IVR' : o.label;
      out.push({ value: o.value, label, ...detail });
    }
    return out;
  }, [isContinuous]);

  /** The TTS family (Simple/DTMF/Call-Patch) shares one parent card. */
  const isTtsFamily =
    values.templateId === '7' ||
    values.templateId === '8' ||
    values.templateId === '9';

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Campaign basics</CardTitle>
          <CardDescription>Give your campaign a name and pick its type.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="campaign-name">
              Campaign name
              <span className="ml-1 text-destructive">*</span>
            </Label>
            <Input
              id="campaign-name"
              value={values.campaignName}
              onChange={(e) => setValues({ campaignName: e.target.value })}
              placeholder="e.g. Diwali promo · 2026-06-12"
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <Label>
              Campaign type
              <span className="ml-1 text-destructive">*</span>
            </Label>
            <div
              role="radiogroup"
              aria-label="Campaign type"
              className={cn(
                'grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-5',
                lockTemplate && 'opacity-60',
              )}
            >
              {availableCards.map((card) => {
                const Icon = card.icon;
                // The TTS parent card highlights for any of 7/8/9.
                const active =
                  card.value === '7'
                    ? isTtsFamily
                    : values.templateId === card.value;
                return (
                  <button
                    key={card.value}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    disabled={lockTemplate}
                    title={card.description}
                    onClick={() =>
                      setValues({
                        // Clicking "TTS" defaults to TTS Simple (7); other
                        // cards write their own templateId.
                        templateId: card.value,
                      })
                    }
                    className={cn(
                      'group relative flex flex-col items-center gap-1 rounded-md border px-2 pb-2 pt-3 text-center transition-all',
                      active
                        ? 'border-primary bg-primary/5 ring-2 ring-primary/30'
                        : 'border-border hover:border-primary/40 hover:bg-accent/30',
                      lockTemplate && 'pointer-events-none',
                    )}
                  >
                    <span
                      aria-hidden
                      className={cn(
                        'absolute right-1.5 top-1.5 h-2.5 w-2.5 rounded-full border transition-colors',
                        active
                          ? 'border-primary bg-primary'
                          : 'border-muted-foreground/40',
                      )}
                    />
                    <div
                      className={cn(
                        'flex h-7 w-7 items-center justify-center rounded',
                        active
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary',
                      )}
                    >
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="flex min-h-[2.75rem] flex-col items-center justify-center gap-0.5">
                      <div className="text-sm font-semibold leading-tight text-foreground">
                        {card.label}
                      </div>
                      <div className="text-[11px] leading-tight text-muted-foreground">
                        {card.description}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            {lockTemplate && (
              <p className="text-xs text-muted-foreground">
                Campaign type can&apos;t be changed after creation.
              </p>
            )}
            {availableCards.length === 0 && (
              <p className="text-xs text-muted-foreground">
                No campaign types are enabled on your account. Ask an admin to enable a
                module.
              </p>
            )}
          </div>

          {isTtsFamily && (
            <div className="space-y-2">
              <Label>TTS flavor</Label>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                {[
                  {
                    value: '7' as TemplateId,
                    label: 'Simple TTS',
                    desc: 'Play a TTS prompt only.',
                  },
                  {
                    value: '8' as TemplateId,
                    label: 'TTS DTMF',
                    desc: 'TTS menu + DTMF input.',
                  },
                  {
                    value: '9' as TemplateId,
                    label: 'TTS Call Patch',
                    desc: 'TTS menu + agent patch.',
                  },
                ].map((opt) => {
                  const active = values.templateId === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      onClick={() => setValues({ templateId: opt.value })}
                      className={cn(
                        'rounded-md border p-3 text-left transition-colors',
                        active
                          ? 'border-primary bg-primary/5 ring-2 ring-primary/30'
                          : 'border-border hover:border-primary/40',
                      )}
                    >
                      <div className="text-sm font-medium">{opt.label}</div>
                      <div className="text-xs text-muted-foreground">{opt.desc}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
